// ===== K-Tec Product Picker Modal =====
// Full-featured product picker that connects to catalogStore & adminStore.
// Used in Quotation Builder when "+ Pick item" is clicked.

import { useState, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Search, X, Package, ShoppingBag, ChevronRight, Plus, AlertCircle } from 'lucide-react';
import { useCatalogStore } from '../store/catalogStore';
import { useAdminStore } from '../store/adminStore';
import type { CatalogProduct, CCTVPackage } from '../types/catalog';

const fmtPrice = (n: number) =>
  `Rs. ${n.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const BRAND_FILTERS = [
  { id: 'all', label: 'All Brands' },
  { id: 'b1', label: 'Dahua' },
  { id: 'b2', label: 'Hikvision' },
  { id: 'b3', label: 'EZVIZ' },
  { id: 'b4', label: 'IMOU' },
  { id: 'b5', label: 'Other / Generic' },
];

const CATEGORY_FILTERS = [
  { id: 'all', label: 'All Categories' },
  { id: 'cam', label: 'CCTV Cameras', match: ['sub1','sub2','sub3','sub4','sub5','sub6','sub7','sub8','sub9','sub10','sub11','sub12'] },
  { id: 'nvr', label: 'NVR / DVR', match: ['sub13','sub14'] },
  { id: 'hdd', label: 'Hard Drives', match: ['sub15'] },
  { id: 'net', label: 'Networking', match: ['sub23','sub24','sub25','sub26','sub27','sub28'] },
  { id: 'alm', label: 'Alarm Systems', match: ['sub18','sub19','sub20','sub21','sub22'] },
  { id: 'smh', label: 'Smart Home', match: ['sub16','sub17','sub22'] },
  { id: 'acc', label: 'Accessories', match: ['sub29','sub30','sub31','sub32','sub33','sub34','sub35','sub36','sub37'] },
  { id: 'pkg', label: 'CCTV Packages', match: [] },
];

interface PickedProduct {
  name: string;
  modelNumber: string;
  warrantyPeriod: string;
  unitPrice: number;
  description: string;
  stock: number;
  brandName: string;
  categoryName: string;
}

interface PackageChoice {
  pkg: CCTVPackage;
  mode: 'single' | 'expand';
}

interface ProductPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called when a catalog product is selected */
  onSelectProduct: (product: PickedProduct) => void;
  /** Called when a package is selected as single line */
  onSelectPackageSingle: (pkg: CCTVPackage) => void;
  /** Called when a package is selected as expanded items */
  onSelectPackageExpanded: (pkg: CCTVPackage, items: PickedProduct[]) => void;
}

export default function ProductPickerModal({
  isOpen, onClose, onSelectProduct, onSelectPackageSingle, onSelectPackageExpanded,
}: ProductPickerModalProps) {
  const { catalogProducts, brands, categories, packages } = useCatalogStore();
  const { products: adminProducts } = useAdminStore();

  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [expandPackageId, setExpandPackageId] = useState<string | null>(null);

  // Helper: get brand name
  const getBrandName = (brandId: string) => brands.find(b => b.id === brandId)?.name || brandId;
  const getCategoryName = (catId: string) => categories.find(c => c.id === catId)?.name || catId;

  // Merge catalog products + admin products (deduplicated by SKU/name)
  const allProducts = useMemo((): PickedProduct[] => {
    const catalogItems: PickedProduct[] = catalogProducts
      .filter(p => p.status === 'active' || !p.status)
      .map(p => ({
        name: p.name,
        modelNumber: p.modelNumber || p.sku || '',
        warrantyPeriod: p.warrantyPeriod || '',
        unitPrice: p.salePrice ?? p.regularPrice ?? 0,
        description: p.shortDescription || p.fullDescription || '',
        stock: (p.stock || 0) - (p.reservedStock || 0),
        brandName: getBrandName(p.brandId),
        categoryName: getCategoryName(p.subcategoryId || p.categoryId),
        brandId: p.brandId,
        subcategoryId: p.subcategoryId || '',
        categoryId: p.categoryId,
        _source: 'catalog' as const,
        _catalogProduct: p,
      }));

    // Add admin products not already in catalog (by name match)
    const catalogNames = new Set(catalogItems.map(p => p.name.toLowerCase()));
    const adminItems: PickedProduct[] = adminProducts
      .filter(p => !catalogNames.has(p.name.toLowerCase()))
      .map(p => ({
        name: p.name,
        modelNumber: p.model || p.sku || '',
        warrantyPeriod: p.warranty ? `${p.warranty} Months` : '',
        unitPrice: p.price || 0,
        description: p.specifications || '',
        stock: (p.stock || 0) - (p.reservedStock || 0),
        brandName: p.brand || '',
        categoryName: p.category || '',
        brandId: '',
        subcategoryId: '',
        categoryId: '',
        _source: 'admin' as const,
        _catalogProduct: null,
      }));

    return [...catalogItems, ...adminItems];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogProducts, adminProducts, brands, categories]);

  // Extended product type with filter fields
  type ExtProduct = PickedProduct & { brandId: string; subcategoryId: string; categoryId: string; _source: string; _catalogProduct: CatalogProduct | null };

  const filteredProducts = useMemo(() => {
    const q = search.toLowerCase().trim();
    const catFilter = CATEGORY_FILTERS.find(c => c.id === categoryFilter);
    const showPackages = categoryFilter === 'pkg';

    if (showPackages) return [];

    return (allProducts as ExtProduct[]).filter(p => {
      const matchSearch = !q || p.name.toLowerCase().includes(q) ||
        p.modelNumber.toLowerCase().includes(q) ||
        p.brandName.toLowerCase().includes(q) ||
        p.categoryName.toLowerCase().includes(q);
      const matchBrand = brandFilter === 'all' || p.brandId === brandFilter;
      const matchCat = !catFilter || catFilter.id === 'all' ||
        (catFilter.match.length > 0 && (catFilter.match.includes(p.subcategoryId) || catFilter.match.includes(p.categoryId)));
      return matchSearch && matchBrand && matchCat;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProducts, search, brandFilter, categoryFilter]);

  const filteredPackages = useMemo(() => {
    if (categoryFilter !== 'all' && categoryFilter !== 'pkg') return [];
    const q = search.toLowerCase().trim();
    return packages.filter(pkg =>
      pkg.isEnabled &&
      (!q || pkg.title.toLowerCase().includes(q) || pkg.description?.toLowerCase().includes(q))
    );
  }, [packages, search, categoryFilter]);

  const showPackages = categoryFilter === 'pkg' || categoryFilter === 'all';

  const handleSelectProduct = (p: PickedProduct) => {
    onSelectProduct(p);
    onClose();
    setSearch('');
    setBrandFilter('all');
    setCategoryFilter('all');
  };

  const handlePackageSingle = (pkg: CCTVPackage) => {
    onSelectPackageSingle(pkg);
    onClose();
    setSearch('');
    setBrandFilter('all');
    setCategoryFilter('all');
  };

  const handlePackageExpand = (pkg: CCTVPackage) => {
    // Build individual items from package
    const items: PickedProduct[] = pkg.items.map(item => {
      // Try to find linked catalog product
      const linked = item.productId ? catalogProducts.find(p => p.id === item.productId) : null;
      return {
        name: linked ? linked.name : (item.customName || 'Package Item'),
        modelNumber: linked?.modelNumber || linked?.sku || '',
        warrantyPeriod: linked?.warrantyPeriod || '',
        unitPrice: item.unitPrice ?? (linked ? (linked.salePrice ?? linked.regularPrice ?? 0) : 0),
        description: linked?.shortDescription || '',
        stock: linked ? (linked.stock - linked.reservedStock) : 999,
        brandName: linked ? getBrandName(linked.brandId) : '',
        categoryName: linked ? getCategoryName(linked.subcategoryId || linked.categoryId) : item.itemType,
      };
    });
    onSelectPackageExpanded(pkg, items);
    onClose();
    setSearch('');
    setBrandFilter('all');
    setCategoryFilter('all');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="picker-backdrop"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-[60]"
            onClick={onClose}
          />
          {/* Modal */}
          <motion.div
            key="picker-modal"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-x-4 top-[5vh] bottom-[5vh] sm:inset-x-8 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-[900px] bg-white rounded-2xl shadow-2xl z-[70] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
              <div>
                <h2 className="font-semibold text-gray-900 text-base">Pick Product</h2>
                <p className="text-xs text-gray-500 mt-0.5">{allProducts.length} products · {packages.filter(p => p.isEnabled).length} packages</p>
              </div>
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {/* Search */}
            <div className="px-5 pt-3 pb-2 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  autoFocus
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/30"
                  placeholder="Search by name, model, brand…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Brand Filters */}
            <div className="px-5 py-2 flex gap-2 overflow-x-auto flex-shrink-0 scrollbar-none">
              {BRAND_FILTERS.map(bf => (
                <button
                  key={bf.id}
                  onClick={() => setBrandFilter(bf.id)}
                  className={`px-3 py-1 text-xs rounded-full font-medium whitespace-nowrap transition-colors ${
                    brandFilter === bf.id
                      ? 'bg-[#7FA86A] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {bf.label}
                </button>
              ))}
            </div>

            {/* Category Filters */}
            <div className="px-5 py-2 flex gap-2 overflow-x-auto flex-shrink-0 scrollbar-none border-b border-gray-100">
              {CATEGORY_FILTERS.map(cf => (
                <button
                  key={cf.id}
                  onClick={() => setCategoryFilter(cf.id)}
                  className={`px-3 py-1 text-xs rounded-lg font-medium whitespace-nowrap transition-colors ${
                    categoryFilter === cf.id
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {cf.label}
                </button>
              ))}
            </div>

            {/* Results */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Products Grid */}
              {filteredProducts.length > 0 && (
                <div className="mb-6">
                  {categoryFilter !== 'pkg' && (
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-3">
                      Products ({filteredProducts.length})
                    </p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {filteredProducts.slice(0, 50).map((p, idx) => {
                      const ep = p as ExtProduct;
                      return (
                        <div
                          key={`${ep._source}-${ep.modelNumber || idx}`}
                          className="flex gap-3 p-3 border border-gray-200 rounded-xl hover:border-[#7FA86A]/40 hover:bg-[#F1F6EE]/30 transition-colors cursor-pointer group"
                          onClick={() => handleSelectProduct(p)}
                        >
                          {/* Product Image / Placeholder */}
                          <div className="w-14 h-14 flex-shrink-0 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                            {ep._catalogProduct?.mainImage ? (
                              <img src={ep._catalogProduct.mainImage} alt={p.name} className="w-full h-full object-cover" />
                            ) : (
                              <Package className="w-6 h-6 text-gray-300" />
                            )}
                          </div>

                          {/* Details */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 leading-tight line-clamp-2">{p.name}</p>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              {p.brandName && (
                                <span className="text-xs text-[#7FA86A] font-medium">{p.brandName}</span>
                              )}
                              {p.categoryName && (
                                <span className="text-xs text-gray-400">{p.categoryName}</span>
                              )}
                            </div>
                            {p.modelNumber && (
                              <p className="text-xs text-gray-500 mt-0.5 font-mono">{p.modelNumber}</p>
                            )}
                            <div className="flex items-center justify-between mt-1.5">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-gray-900">{fmtPrice(p.unitPrice)}</span>
                                {p.warrantyPeriod && (
                                  <span className="text-xs bg-green-50 text-green-700 px-1.5 py-0.5 rounded">{p.warrantyPeriod}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5">
                                {p.stock <= 0 ? (
                                  <span className="text-xs text-red-500 flex items-center gap-0.5">
                                    <AlertCircle className="w-3 h-3" /> Out of stock
                                  </span>
                                ) : (
                                  <span className={`text-xs font-medium ${p.stock <= 5 ? 'text-[#658C53]' : 'text-gray-500'}`}>
                                    Stock: {p.stock}
                                  </span>
                                )}
                                <button
                                  onClick={e => { e.stopPropagation(); handleSelectProduct(p); }}
                                  className="w-6 h-6 flex items-center justify-center bg-[#7FA86A] text-white rounded-full hover:bg-[#658C53] transition-colors opacity-0 group-hover:opacity-100"
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Packages */}
              {showPackages && filteredPackages.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-3">
                    CCTV Packages ({filteredPackages.length})
                  </p>
                  <div className="space-y-3">
                    {filteredPackages.map(pkg => (
                      <div
                        key={pkg.id}
                        className="p-4 border border-gray-200 rounded-xl hover:border-[#7FA86A]/40 hover:bg-[#F1F6EE]/20 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex gap-3">
                            <div className="w-10 h-10 flex-shrink-0 bg-purple-50 rounded-lg flex items-center justify-center">
                              <ShoppingBag className="w-5 h-5 text-purple-500" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-900">{pkg.title}</p>
                              {pkg.description && (
                                <p className="text-xs text-gray-500 mt-0.5">{pkg.description}</p>
                              )}
                              <div className="flex items-center gap-3 mt-1.5">
                                {pkg.sellingPrice > 0 && (
                                  <span className="text-sm font-bold text-gray-900">{fmtPrice(pkg.sellingPrice)}</span>
                                )}
                                {pkg.cameraQty > 0 && (
                                  <span className="text-xs text-gray-500">{pkg.cameraQty} cameras</span>
                                )}
                                {pkg.items.length > 0 && (
                                  <span className="text-xs text-gray-500">{pkg.items.length} items</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Package Actions */}
                        {expandPackageId === pkg.id ? (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <p className="text-xs font-medium text-gray-600 mb-2">How to add this package?</p>
                            <div className="flex gap-2 flex-wrap">
                              <button
                                onClick={() => handlePackageSingle(pkg)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                              >
                                <Package className="w-3 h-3" /> Add as One Line
                              </button>
                              {pkg.items.length > 0 && (
                                <button
                                  onClick={() => handlePackageExpand(pkg)}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
                                >
                                  <ChevronRight className="w-3 h-3" /> Expand {pkg.items.length} Items
                                </button>
                              )}
                              <button
                                onClick={() => setExpandPackageId(null)}
                                className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setExpandPackageId(pkg.id)}
                            className="mt-3 flex items-center gap-1.5 text-xs text-[#7FA86A] hover:text-[#658C53] font-medium"
                          >
                            <Plus className="w-3 h-3" /> Add Package
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {filteredProducts.length === 0 && filteredPackages.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <Package className="w-12 h-12 mb-3 opacity-30" />
                  <p className="text-sm font-medium">No products found</p>
                  <p className="text-xs mt-1">Try adjusting your search or filters</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-gray-100 flex-shrink-0 bg-gray-50">
              <p className="text-xs text-gray-400 text-center">
                Click any product to add it to the quotation row • All products editable in Product Catalog
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
